/**
 * SalesArchive.tsx — Sales / Archive (closed deals, slice 3b).
 *
 * Spec: docs/design/sales-build-design.md §5.F. The mockup's Archive layout
 * is a full-file gap (§A.4.4), so this is the house-style table — reconcile
 * layout if the full mockup ever surfaces; the semantics are settled.
 *
 * Data honesty: TWO stage-filtered list calls (closed_won + closed_lost via
 * fetchClosedDeals), never a client filter of the limit-200 unfiltered list;
 * every cell renders only fields the read path exposes. The invoice number
 * is a lazy per-row single-invoice GET (the Deal Detail F2 pattern) — only
 * won rows fetch, and a failed lookup says so instead of fabricating.
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { useState } from 'react'
import { Search } from 'lucide-react'
import { SectionTitle, Avatar, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { useAsync, fetchClosedDeals, getIssuedInvoice } from '../../api'
import { accent, success, danger } from '../../../../theme/tokens'
import { money } from '../../../../lib/money'

// The board card's outcome badge, table-sized (§5.D: success/danger families,
// same encoding both surfaces).
function OutcomeBadge({ lost }: { lost: boolean }) {
  return (
    <span className="k-badge"
      style={lost ? { background: danger.bg, color: danger.text } : { background: success.bg, color: success.text }}>
      <span className="k-badge-dot" />{lost ? 'LOST' : 'WON'}
    </span>
  )
}

// Invoice cell (won rows only): lazy number lookup, navigation to Finance →
// Issued Invoices on click (C5-3 — number + tab nav, no per-invoice deep
// link exists). stopPropagation: the row's own click drills to the deal.
function InvoiceCell({ invoiceId, onNavigate }: { invoiceId: string; onNavigate?: (dest: string) => void }) {
  const { data, loading, error } = useAsync(() => getIssuedInvoice(invoiceId), [invoiceId])
  if (loading) return <span className="text-ink-muted">Loading…</span>
  if (error || !data) return <span className="text-ink-muted">Unavailable</span>
  if (!onNavigate) return <>{data.invoiceNumber}</>
  return (
    <button type="button" title="View in Issued Invoices"
      onClick={e => { e.stopPropagation(); onNavigate('finance.issued-invoices') }}
      className="font-medium hover:underline" style={{ color: accent.DEFAULT }}>
      {data.invoiceNumber}
    </button>
  )
}

export function SalesArchive({ onSelectDeal, onNavigate }: {
  onSelectDeal?: (id: string) => void
  onNavigate?: (dest: string) => void
}) {
  const [search, setSearch] = useState('')
  const { data, loading, error } = useAsync(fetchClosedDeals, [])

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const deals = data ?? []
  const filtered = deals.filter(d =>
    search === '' || d.name.toLowerCase().includes(search.toLowerCase()) || d.company.toLowerCase().includes(search.toLowerCase())
  )
  const wonCount = deals.filter(d => d.stage === 'Closed Won').length
  const lostCount = deals.filter(d => d.stage === 'Closed Lost').length

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title="Archive"
        subtitle={`${deals.length} closed deals · ${wonCount} won · ${lostCount} lost`}
        action={
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search closed deals…"
              className="h-8 pl-7 pr-3 text-12 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-muted focus:outline-none focus:border-accent w-52"
            />
          </div>
        }
      />

      <div className="k-card">
        {deals.length === 0 ? (
          <EmptyState message="No closed deals yet" hint="Deals closed as won or lost from the Pipeline land here." />
        ) : (
          <div className="overflow-x-auto">
            <table className="closr-table sales-table w-full">
              <thead>
                <tr>
                  <th className="text-left">Deal</th>
                  <th className="text-left">Company</th>
                  <th className="text-left">Value</th>
                  <th className="text-left">Outcome</th>
                  <th className="text-left">Closed</th>
                  <th className="text-left">Lost reason</th>
                  <th className="text-left">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(d => {
                  const lost = d.stage === 'Closed Lost'
                  return (
                    <tr key={d.id} className="cursor-pointer" onClick={() => onSelectDeal?.(d.id)}>
                      <td>
                        <div className="text-13 font-semibold text-ink-primary">{d.name}</div>
                      </td>
                      <td>
                        <div className="flex items-center gap-2 text-13 text-ink-body">
                          <Avatar name={d.owner} size={22} />{d.company}
                        </div>
                      </td>
                      <td className="text-13 font-semibold text-ink-primary">{money(d.amount / 100, d.currency)}</td>
                      <td><OutcomeBadge lost={lost} /></td>
                      <td className="text-12 text-ink-secondary">{d.closedDate || '—'}</td>
                      <td className="text-12 text-ink-secondary">{lost ? (d.lostReason || '—') : '—'}</td>
                      <td className="text-12">
                        {!lost && d.issuedInvoiceId
                          ? <InvoiceCell invoiceId={d.issuedInvoiceId} onNavigate={onNavigate} />
                          : <span className="text-ink-muted">—</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="p-5 text-13 text-ink-muted">No closed deals match the search.</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
